import React, { useEffect, useState } from 'react';

import { BodySample, FaceSample } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import st from './MultiSample.module.sass';

interface SampleData {
  imageUrl?: string;
  type: 'face' | 'body';
}

interface MultiSampleProps extends WithDataTestid {
  primary?: SampleData;
  secondary?: SampleData;
  className?: string;
  sampleType?: 'multi' | 'face' | 'body';
  mainSample?: 'face' | 'body';
}

// redux-free замена shared MultiSample: рендерит uikit FaceSample/BodySample
// напрямую, без Can/connect. Используется в point3, где нет redux-store.
export function MultiSample({
  primary = { type: 'face' },
  secondary = { type: 'body' },
  className = undefined,
  sampleType = 'multi',
  mainSample = 'face',
  'data-testid': testId = undefined,
}: MultiSampleProps): React.JSX.Element {
  const [mainView, setMainView] = useState<'primary' | 'secondary'>('primary');

  useEffect(() => {
    if (!primary.imageUrl) {
      setMainView('secondary');
      return;
    }

    if (sampleType === 'multi') {
      setMainView(mainSample === 'face' ? 'primary' : 'secondary');
    } else {
      setMainView(sampleType === 'face' ? 'primary' : 'secondary');
    }
  }, [primary, secondary, sampleType, mainSample]);

  const viewMap = {
    primary: { main: primary, inside: secondary },
    secondary: { main: secondary, inside: primary },
  };

  const onInsideImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMainView((view) => (view === 'primary' ? 'secondary' : 'primary'));
  };

  const renderSample = (data: SampleData, inside = false) => {
    const sampleClassName = cn(st.Sample, { [st.Sample_inside]: inside });
    if (data.type === 'body') {
      return (
        <BodySample
          className={sampleClassName}
          src={data.imageUrl}
        />
      );
    }
    return (
      <FaceSample
        className={sampleClassName}
        src={data.imageUrl}
      />
    );
  };

  const { main } = viewMap[mainView];
  const { inside } = viewMap[mainView];

  return (
    <div
      className={cn(st.MultiSample, className)}
      data-testid={testId}
    >
      <div className={st.SampleContainer}>
        <div
          className={st.BackgroundSample}
          style={{ backgroundImage: `url(${main?.imageUrl})` }}
        />
        {renderSample(main)}
      </div>

      {sampleType === 'multi' && !!inside?.imageUrl && (
        <button
          aria-label="Toggle inside image"
          className={st.Toggle}
          onClick={onInsideImageClick}
          type="button"
        >
          <div
            className={st.BackgroundSample}
            style={{ backgroundImage: `url(${inside.imageUrl})` }}
          />
          {renderSample(inside, true)}
        </button>
      )}
    </div>
  );
}
